@if (!loadingPage()) {
  <div class="page">
    <div class="header">
      <h1 class="header-title">
        <mat-icon>manage_accounts</mat-icon>
        <span>Medewerkers</span>
      </h1>
      @if (isAdministrator()) {
        <div class="header-buttons">
          <button
            class="header-button export-button"
            mat-icon-button
            aria-label="Importeren en exporteren"
            (click)="openImportExport()"
            [class.spinner]="processing()"
            [disabled]="processing()"
          >
            <mat-icon>import_export</mat-icon>
            <div class="button-text">Exporteren/Importeren</div>
          </button>
        </div>
      }
    </div>
    <mat-card class="content">
      <div class="content-header">
        <h2>
          {{ isAdministrator() && showHidden() ? 'Inactieve' : 'Actieve' }} medewerkers
          <span aria-live="polite" aria-atomic="true">({{ filteredUsers().length }})</span>
        </h2>
        @if (isAdministrator()) {
          <div class="header-right">
            <mat-form-field class="mat-form-field-no-subscript">
              <mat-label class="placeholder">Zoeken</mat-label>
              <input matInput [ngModel]="searchQuery()" (ngModelChange)="searchQuery.set($event)" />
              <button
                mat-icon-button
                matSuffix
                aria-label="Zoekopdracht wissen"
                (click)="clearSearch(); $event.stopPropagation()"
              >
                <mat-icon>clear</mat-icon>
              </button>
            </mat-form-field>
          </div>
        }
      </div>
      @if (isAdministrator()) {
        <div class="filters" role="group" aria-label="Medewerkersfilters">
          <div class="filter-buttons">
            <button
              type="button"
              mat-stroked-button
              class="filter-button primary-color"
              [matMenuTriggerFor]="roleFilterMenu"
              [matTooltip]="roleFilterSummary()"
            >
              @if (roleFilterActive()) {
                <mat-icon>check</mat-icon>
              }
              <span class="filter-label" [class.inactive]="!roleFilterActive()">
                {{ roleFilterActive() ? roleFilterSummary() : 'Rollen' }}
              </span>
              <mat-icon iconPositionEnd>arrow_drop_down</mat-icon>
            </button>
            <mat-menu #roleFilterMenu="matMenu" class="role-filters" aria-label="Medewerkersrollen">
              @for (role of filterRoles; track role) {
                <button
                  type="button"
                  mat-menu-item
                  role="menuitemcheckbox"
                  [attr.aria-checked]="selectedRoles().includes(role)"
                  (click)="$event.stopPropagation(); toggleRole(role)"
                  (keydown.space)="$event.preventDefault(); $event.stopPropagation(); toggleRole(role)"
                >
                  <mat-icon>{{ selectedRoles().includes(role) ? 'check_box' : 'check_box_outline_blank' }}</mat-icon>
                  {{ getRoleLabel(role) }}
                </button>
              }
            </mat-menu>
            @if (hasPersonalDataAccess()) {
              @for (field of dateFields; track field.value) {
                <div class="date-filter">
                  <button
                    type="button"
                    mat-stroked-button
                    class="filter-button primary-color"
                    aria-haspopup="dialog"
                    [attr.aria-expanded]="datePicker.opened"
                    (click)="openDateFilter(field.value, datePicker)"
                    [matTooltip]="selectedDateField() === field.value ? dateFilterSummary() : field.label"
                  >
                    @if (activeDateRange()?.field === field.value) {
                      <mat-icon>check</mat-icon>
                    }
                    <span class="filter-label" [class.inactive]="activeDateRange()?.field !== field.value">
                      {{ selectedDateField() === field.value ? dateFilterSummary() : field.label }}
                    </span>
                    <mat-icon iconPositionEnd>date_range</mat-icon>
                  </button>
                  <mat-date-range-input class="date-filter-input" [rangePicker]="datePicker" aria-hidden="true">
                    <input
                      matStartDate
                      [formControl]="dateRangeDrafts[field.value].start"
                      tabindex="-1"
                      readonly
                      (dateChange)="applyDateFilter(field.value)"
                    />
                    <input
                      matEndDate
                      [formControl]="dateRangeDrafts[field.value].end"
                      tabindex="-1"
                      readonly
                      (dateChange)="applyDateFilter(field.value)"
                    />
                  </mat-date-range-input>
                  <mat-date-range-picker #datePicker [aria-label]="field.label">
                    <mat-datepicker-actions>
                      <button type="button" mat-button (click)="clearDateFilter(); datePicker.close()">Wissen</button>
                      <button type="button" mat-flat-button class="primary-color" matDatepickerApply>Toepassen</button>
                    </mat-datepicker-actions>
                  </mat-date-range-picker>
                </div>
              }
            }
          </div>
          <div class="filter-actions">
            @if (roleFilterActive() || selectedDateField() || dateStartControl.value || dateEndControl.value) {
              <button
                type="button"
                mat-icon-button
                aria-label="Alle filters wissen"
                matTooltip="Alle filters wissen"
                (click)="clearAllFilters()"
              >
                <mat-icon>filter_alt_off</mat-icon>
              </button>
            }
            <button
              type="button"
              mat-icon-button
              [attr.aria-label]="showHidden() ? 'Inactieve medewerkers verbergen' : 'Inactieve medewerkers tonen'"
              [matTooltip]="
                showHidden() ? 'Laat inactieve medewerkers niet meer zien' : 'Laat inactieve medewerkers zien'
              "
              (click)="showHidden.set(!showHidden())"
            >
              <mat-icon>{{ showHidden() ? 'visibility_off' : 'visibility' }}</mat-icon>
            </button>
          </div>
        </div>
      }
      <div class="list-items">
        <div class="list-item-row headers">
          <div class="list-item-col photo-column"></div>
          <div class="list-item-col">Naam</div>
          @if (isAdministrator()) {
            <div class="list-item-col">E-mailadres</div>
          }
          <div class="list-item-col phone-column" [class.hide-column-3]="isAdministrator()">Telefoonnummer</div>
          @if (hasPersonalDataAccess()) {
            @if (isAdministrator()) {
              <div class="list-item-col hide-column-2">Startdatum</div>
              <div class="list-item-col hide-column-2">VOG-datum</div>
              <div class="list-item-col hide-column-2">Rijtestdatum</div>
            }
          }
          @if (isAdministrator()) {
            <div class="list-item-col role-column">Rol</div>
            @if (hasPersonalDataAccess()) {
              <div class="list-item-col hide-column remarks-column">Opmerking (Algemeen)</div>
            }
            <div class="list-item-col list-item-actions"></div>
          }
        </div>
        <div class="list-wrapper">
          @for (user of filteredUsers(); track user.id) {
            @if (isAdministrator()) {
              <div
                class="list-item-row"
                role="button"
                tabindex="0"
                aria-label="Medewerker openen"
                (click)="editUser(user)"
                (keydown.enter)="editUser(user)"
                (keydown.space)="$event.preventDefault(); editUser(user)"
              >
                <ng-container *ngTemplateOutlet="employeeRow; context: { $implicit: user }"></ng-container>
              </div>
            } @else {
              <div class="list-item-row staff-row">
                <ng-container *ngTemplateOutlet="employeeRow; context: { $implicit: user }"></ng-container>
              </div>
            }
          } @empty {
            @if (!loading()) {
              <p class="empty-results" role="status">Geen medewerkers gevonden.</p>
            }
          }
          <div class="list-item-row list-end-spacer"></div>
        </div>
      </div>
    </mat-card>
  </div>
  @if (isAdministrator()) {
    <button mat-fab extended aria-label="Medewerker toevoegen" (click)="editUser()">
      <mat-icon>add</mat-icon>
    </button>
    <input hidden type="file" accept=".xlsx" #uploader (change)="uploadedFile($event)" />
  }
}
@if (loadingPage()) {
  <div class="loading-wrapper" role="status" aria-live="polite" aria-atomic="true">
    <div class="loading">
      <mat-spinner></mat-spinner>
    </div>
  </div>
}

@if (importing() || addressValidationService.isBulkValidating()) {
  <div class="validation-overlay">
    <div class="validation-progress-container" role="status" aria-live="polite" aria-atomic="true">
      <h3>Importeren...</h3>
      <mat-progress-bar
        mode="determinate"
        [value]="
          addressValidationService.isBulkValidating() ? addressValidationService.bulkProgress() : importProgress()
        "
      ></mat-progress-bar>
      <p class="progress-text">
        {{ addressValidationService.isBulkValidating() ? addressValidationService.bulkProgress() : importProgress() }}%
        voltooid
      </p>
    </div>
  </div>
}
<ng-template #employeeRow let-user>
  <div class="list-item-col photo-column">
    @if (canShowEmployeePhotos() && user.photoUrl) {
      <img class="employee-avatar" [src]="user.photoUrl" alt="Profielfoto" />
    } @else {
      <div class="circle-icon primary-bg" aria-hidden="true">
        {{ (user.name ?? '?').charAt(0).toUpperCase() }}
      </div>
    }
  </div>
  <div class="list-item-col name-column">
    <span class="employee-name">{{ user.name }} {{ user.lastName }}</span>
  </div>
  @if (isAdministrator()) {
    <div class="list-item-col email-column">{{ user.email }}</div>
  }
  <div class="list-item-col phone-column" [class.hide-column-3]="isAdministrator()">
    {{ user.phone ?? user.phoneNumber ?? '' }}
  </div>
  @if (isAdministrator() && hasPersonalDataAccess()) {
    <div class="list-item-col hide-column-2">{{ formatDate(user.startDate) }}</div>
    <div class="list-item-col hide-column-2">{{ formatDate(user.vogDate) }}</div>
    <div class="list-item-col hide-column-2">{{ formatDate(user.driversTestDate) }}</div>
  }
  @if (isAdministrator()) {
    <div class="list-item-col role-column">
      <mat-chip-set>
        @for (role of user.roles; track role) {
          <mat-chip class="role text-truncate">{{ getRoleLabel(role) }}</mat-chip>
        }
        @if (user.passenger) {
          <mat-chip class="role text-truncate">Passagier</mat-chip>
        }
        @if ((!user.roles || user.roles.length === 0) && !user.passenger) {
          <mat-chip class="role text-truncate none">Geen</mat-chip>
        }
      </mat-chip-set>
    </div>
    @if (hasPersonalDataAccess()) {
      <div class="list-item-col hide-column remarks-column">{{ user.hiddenRemarks }}</div>
    }
    <div class="list-item-col list-item-actions">
      <button mat-icon-button aria-label="Medewerker bewerken" (click)="editUser(user); $event.stopPropagation()">
        <mat-icon>edit</mat-icon>
      </button>
    </div>
  }
</ng-template>
